import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import httpClient from "../../services/httpClient";
import Logger from "../../utils/Logger";
import { useAuth } from "../../AuthContext";
import orangeSortIcon from "../../media/orange-sort-icon.svg";
import deleteButtonIcon from "../../media/delete-button.svg";
import trashButtonIcon from "../../media/trash-button.svg";
import editButtonIcon from "../../media/edit-button.svg";
import agreeButtonIcon from "../../media/agree-button.svg";
import backButtonIcon from "../../media/back-button.svg";
import "../../styleSheet/main/admin/manageRooms/manageRooms.scss";
import { createPortal } from "react-dom";

const ManageRooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const navigate = useNavigate();
  const { userData } = useAuth();

  // Sprawdź czy użytkownik jest adminem
  useEffect(() => {
    if (userData.userRole !== "admin") {
      navigate("/");
    }
  }, [userData, navigate]);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get("/Rooms", { withAuth: true });
      setRooms(response.data);
      setError(null);
    } catch (err) {
      Logger.error("Błąd podczas pobierania sal:", err);
      setError("Nie udało się pobrać listy sal konferencyjnych");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const handleAddRoom = () => {
    navigate("/administrator/sale-konferencyjne/nowa-sala");
  };

  const handleEditClick = (room) => {
    setEditingId(room.id);
    setEditValues({
      name: room.name,
      email: room.emailAddress,
      colorHex: room.colorHex || "#ff9900",
    });
  };

  const handleEditChange = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleAgreeEdit = async (room) => {
    try {
      await httpClient.put(
        `/Rooms/${room.id}`,
        {
          name: editValues.name,
          email: editValues.email,
          colorHex: editValues.colorHex,
        },
        { withAuth: true },
      );
      setEditingId(null);
      setEditValues({});
      fetchRooms();
    } catch (err) {
      Logger.error("Błąd podczas edycji sali:", err);
      setError("Nie udało się zaktualizować sali");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleDeleteClick = (room) => {
    setRoomToDelete(room);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!roomToDelete) return;

    setIsDeleting(true);
    try {
      await httpClient.delete(`/Rooms/${roomToDelete.id}`, { withAuth: true });
      setShowDeleteConfirmation(false);
      setRoomToDelete(null);
      fetchRooms();
    } catch (err) {
      Logger.error("Błąd podczas usuwania sali:", err);
      setError("Nie udało się usunąć sali");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setRoomToDelete(null);
  };

  const handleBackClick = () => {
    navigate("/rezerwacje/sale-konferencyjne");
  };

  const renderDeleteConfirmationModal = () => {
    if (!showDeleteConfirmation || !roomToDelete) return null;

    return createPortal(
      <div className="confirmation-overlay">
        <div className="confirmation-box">
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt="Close"
            onClick={handleCancelDelete}
          />
          <p>Czy na pewno chcesz usunąć salę konferencyjną?</p>
          <div className="confirmation-details">
            <p>
              <strong>Nazwa:</strong> {roomToDelete.name}
            </p>
            <p>
              <strong>Email:</strong> {roomToDelete.emailAddress}
            </p>
            <p className="warning-text">
              <strong>Uwaga:</strong> Usunięcie sali spowoduje również usunięcie
              wszystkich związanych z nią rezerwacji!
            </p>
          </div>
          <div className="confirmation-buttons">
            {isDeleting ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Usuwanie...</p>
              </div>
            ) : (
              <>
                <button onClick={handleConfirmDelete}>TAK</button>
                <button onClick={handleCancelDelete}>NIE</button>
              </>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  if (loading) {
    return (
      <div className="manage-rooms-container">
        <p>Ładowanie...</p>
      </div>
    );
  }

  return (
    <div className="manage-rooms-container">
      <div className="manage-rooms-header">
        <h1>Zarządzanie salami konferencyjnymi</h1>
        <div
          className="orange-sort-icon"
          onClick={handleBackClick}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hover ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>

      <button className="add-room-button" onClick={handleAddRoom}>
        Dodaj nową salę
      </button>

      {error && <div className="error-message">{error}</div>}

      <div className="rooms-list">
        {rooms.length === 0 ? (
          <div className="no-rooms">Brak sal w bazie danych</div>
        ) : (
          <table className="rooms-table">
            <thead>
              <tr>
                <th>Nazwa sali</th>
                <th>Email</th>
                <th className="color-column">Kolor</th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((room) => (
                <tr key={room.id}>
                  {editingId === room.id ? (
                    <>
                      <td>
                        <input
                          type="text"
                          className="table-edit-input"
                          value={editValues.name}
                          onChange={(e) =>
                            handleEditChange("name", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="email"
                          className="table-edit-input"
                          value={editValues.email}
                          onChange={(e) =>
                            handleEditChange("email", e.target.value)
                          }
                        />
                      </td>
                      <td className="color-column">
                        <input
                          type="color"
                          className="table-color-input"
                          value={editValues.colorHex}
                          onChange={(e) =>
                            handleEditChange("colorHex", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <div className="actions">
                          <img
                            className="delete-btn"
                            src={trashButtonIcon}
                            alt="Usuń"
                            onClick={() => handleDeleteClick(room)}
                          />
                          <img
                            className="agree-btn"
                            src={agreeButtonIcon}
                            alt="Zatwierdź"
                            onClick={() => handleAgreeEdit(room)}
                          />
                          <img
                            className="back-btn"
                            src={backButtonIcon}
                            alt="Anuluj"
                            onClick={handleCancelEdit}
                          />
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{room.name}</td>
                      <td>{room.emailAddress}</td>
                      <td className="color-column">
                        {room.colorHex && (
                          <span
                            className="color-swatch"
                            style={{ backgroundColor: room.colorHex }}
                            title={room.colorHex}
                          />
                        )}
                      </td>
                      <td className="actions">
                        <img
                          className="delete-btn"
                          src={trashButtonIcon}
                          alt="Usuń"
                          onClick={() => handleDeleteClick(room)}
                        />
                        <img
                          className="edit-btn"
                          src={editButtonIcon}
                          alt="Edytuj"
                          onClick={() => handleEditClick(room)}
                        />
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {renderDeleteConfirmationModal()}
    </div>
  );
};

export default ManageRooms;
